import React from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const PROJECT_LOGOS = {
  'ד החדשה': '/logos/aura_logo.jpg',
  'אייזיקחריף': '/logos/aizik_harif.png',
  'ג החדשה': '/logos/jaffag.png',
  'סקייליין בת ים': '/logos/aura_logo.jpg',
  'ראשון החדשה': '/logos/rishon.jpg',
  'אפלטון 6': '/logos/aplaton6.jpeg',
  'שארית ישראל': '/logos/sherit_israel.jpg',
};

// "ג החדשה" is the literal project_name stored in the DB (used for API calls
// and matching) — this only affects what's shown to the user.
const PROJECT_DISPLAY_NAMES = { 'ג החדשה': "ג' החדשה", 'אייזיקחריף': 'אייזיק חריף' };
const displayName = n => PROJECT_DISPLAY_NAMES[n] || n;

export default function TenantProjects() {
  const navigate = useNavigate();
  let projects = [];
  try { projects = [...new Set(JSON.parse(localStorage.getItem('tenantProjects') || '[]') || [])]; } catch { localStorage.removeItem('tenantProjects'); }
  const phone = localStorage.getItem('tenantPhone') || '';

  const [apartments, setApartments] = React.useState(null); // null = not chosen yet; [] or [1] = skip; [2+] = show picker
  const [pendingProject, setPendingProject] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  // Set once the final project+apartment is known (whether that took zero
  // pickers, one, or two) — shows a 3s "welcome" screen, then navigates in.
  const [welcomeTarget, setWelcomeTarget] = React.useState(null);
  const tenantName = localStorage.getItem('tenantName') || '';

  React.useEffect(() => {
    if (!welcomeTarget) return;
    const t = setTimeout(() => navigate(`/portal/${encodeURIComponent(welcomeTarget)}`), 3000);
    return () => clearTimeout(t);
  }, [welcomeTarget]); // eslint-disable-line

  React.useEffect(() => {
    if (!projects.length) { navigate('/portal'); return; }
    if (projects.length === 1) { chooseProject(projects[0]); }
  }, []); // eslint-disable-line

  // A phone can be linked to more than one apartment WITHIN the same project
  // (e.g. an investor owning two units) — these can't be told apart by project
  // name alone, so after picking a project we check how many apartments it
  // actually has and show a second picker only when there's more than one.
  async function chooseProject(name) {
    setPendingProject(name);
    setLoading(true);
    try {
      const r = await api.get(`/portal/tenant-project?phone=${encodeURIComponent(phone)}&projectName=${encodeURIComponent(name)}`);
      const apts = r.data.apartments || [];
      // Remembered so the portal's logout button knows whether to ask "log out
      // or just go back to the list" (only relevant when there's a list to go
      // back to — i.e. more than one project or more than one apartment here).
      localStorage.setItem('tenantMultiApt', apts.length > 1 ? '1' : '0');
      if (apts.length > 1) {
        setApartments(apts);
      } else {
        enterProject(name, apts[0]);
      }
    } catch {
      enterProject(name, null);
    }
    setLoading(false);
  }

  function enterProject(name, apt) {
    // A real tenant login never explicitly reset the last-viewed tab, so
    // reopening the portal (any project) silently restored whatever tab
    // (e.g. "קבצים") the tenant happened to be on last time instead of
    // landing on the home screen — this is the one place every entry into
    // a project's portal funnels through, so it's the right spot to reset it.
    localStorage.setItem('tenantTab', 'home');
    localStorage.setItem('tenantProject', name);
    if (apt) {
      localStorage.setItem('tenantBAddr', apt.buildingAddress || '');
      localStorage.setItem('tenantBNum', String(apt.buildingNumber ?? ''));
      localStorage.setItem('tenantAptNum', String(apt.apartmentNumber ?? ''));
    } else {
      ['tenantBAddr', 'tenantBNum', 'tenantAptNum'].forEach(k => localStorage.removeItem(k));
    }
    setWelcomeTarget(name);
  }

  function logout() {
    ['tenantPhone', 'tenantToken', 'tenantName', 'tenantProject', 'tenantProjects', 'tenantBAddr', 'tenantBNum', 'tenantAptNum', 'tenantTab', 'tenantMultiApt'].forEach(k => localStorage.removeItem(k));
    navigate('/portal');
  }

  if (welcomeTarget) {
    return (
      <div style={{ minHeight: '100vh', background: '#1a4a8a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', direction: 'rtl', color: 'white', textAlign: 'center', padding: 24 }}>
        <div style={{ fontSize: '2rem', fontWeight: 800, marginBottom: 10 }}>
          שלום{tenantName ? ` ${tenantName}` : ''}
        </div>
        <div style={{ fontSize: '1.1rem', opacity: 0.9 }}>ברוך הבא למערכת מבט</div>
      </div>
    );
  }

  if (!projects.length) return null;
  if (projects.length === 1 && apartments === null) return null; // auto-choosing, nothing to render yet
  if (apartments === null && loading) return null;

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', direction: 'rtl' }}>
      <div style={{ background: '#1a4a8a', color: 'white', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>פורטל דיירים</h1>
        <button onClick={logout} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem' }}>יציאה</button>
      </div>
      <div style={{ maxWidth: 700, margin: '48px 24px 48px auto', padding: '0 16px' }}>
        {apartments ? (<>
          <h2 style={{ textAlign: 'right', color: '#1f2937', marginBottom: 8 }}>יש לך כמה דירות ב"{displayName(pendingProject)}"</h2>
          <p style={{ textAlign: 'right', color: '#6b7280', marginBottom: 32 }}>בחר דירה לכניסה</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'flex-start' }}>
            {apartments.map((apt, i) => (
              <button key={i} onClick={() => enterProject(pendingProject, apt)}
                style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 18px', minWidth: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', transition: 'box-shadow 0.15s, transform 0.15s' }}
                onMouseOver={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseOut={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.07)'; e.currentTarget.style.transform = 'none'; }}>
                <span style={{ fontSize: '2rem' }}>🏠</span>
                <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1f2937', textAlign: 'center' }}>{apt.buildingAddress} {apt.buildingNumber}</span>
                <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>דירה {apt.apartmentNumber}</span>
              </button>
            ))}
          </div>
        </>) : (<>
          <h2 style={{ textAlign: 'right', color: '#1f2937', marginBottom: 8 }}>הפרויקטים שלך</h2>
          <p style={{ textAlign: 'right', color: '#6b7280', marginBottom: 32 }}>בחר פרויקט לכניסה</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'flex-start' }}>
            {projects.map(name => (
              <button key={name} onClick={() => chooseProject(name)}
                style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 14, padding: '24px 16px', width: 160, height: 160, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', transition: 'box-shadow 0.15s, transform 0.15s' }}
                onMouseOver={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseOut={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.07)'; e.currentTarget.style.transform = 'none'; }}>
                {PROJECT_LOGOS[name]
                  ? <img src={PROJECT_LOGOS[name]} alt={name} style={{ height: 80, width: 80, objectFit: 'contain' }} />
                  : <span style={{ fontSize: '3rem' }}>🏢</span>
                }
                <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1f2937', textAlign: 'center' }}>{displayName(name)}</span>
              </button>
            ))}
          </div>
        </>)}
      </div>
    </div>
  );
}
